import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    ActivatedRoute,
    ParamMap,
    Router,
    RouterEvent,
    Event,
    NavigationEnd
} from '@angular/router';
import {
    QueryParamConfig,
    SbEventListener,
    SbHost,
    Sbmessage,
    SbWidgetContext
} from '@models';
import {
    SbDataService,
    SbEvent,
    SbEventBusService,
    SbStorageService
} from '../../services';
import { get } from 'dot-prop';
import { Subject, Subscription, timer, concat } from 'rxjs';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import {
    LocalStorageParams,
    LocalStorageService
} from '../../../shared/services/local-storage.service';

@Component({
    selector: 'app-base-widget',
    templateUrl: './base-widget.component.html',
    styleUrls: ['./base-widget.component.scss']
})
export class BaseWidgetComponent implements OnInit, OnDestroy {
    @Input() context: SbWidgetContext;
    @Output() headerInfo = new EventEmitter<string>();
    public isLoading: boolean = false;
    public isSynchronizationOn: boolean = false;
    public isSynchronizing: boolean = false;
    public lastSynchronization: Date;
    protected autoTriggerDataLoad = false;
    protected subscriptions: Subscription[] = [];
    private optionalRouteParams: ParamMap;
    private prevParams;
    private dataCache: { [key: string]: { data: any; timestamp: number } } = {};
    private cacheTiming = 15 * 1000;
    noDataMessage = false;

    /**
     * Need to store these for reload of data during navigation changes where we don't have access to the original state.
     */
    private lastUsedHttpParams: HttpParams;
    msgs: Sbmessage[] = [];
    endPolling$ = new Subject<void>();
    public responseHandler: (response: any) => void;
    public responseErrorHandler: (error: any) => void = this.defaultErrorHandler;
    constructor(
        protected dataService: SbDataService,
        protected localStorageService: LocalStorageService,
        protected eventBus: SbEventBusService,
        protected activatedRoute: ActivatedRoute,
        protected router?: Router
    ) {
        this.activatedRoute.queryParamMap.subscribe((queryParamMap) => {
            this.optionalRouteParams = queryParamMap;
        });
        this.prevParams = this.activatedRoute.snapshot.queryParams;
    }

    predicate = (newParams) => {
        const previousEmpty = Object.keys(this.prevParams).length === 0;
        return (
            !previousEmpty &&
            JSON.stringify(newParams) !== JSON.stringify(this.prevParams)
        );
    };

    ngOnInit() {
        if (this.context.filterListeners) {
            this.context.filterListeners.forEach((filterListener) =>
                this.registerListenerFromContext(filterListener)
            );
            if (this.autoTriggerDataLoad) {
                this.loadInitialData();
            }
            this.activatedRoute.queryParams
                .pipe(filter(this.predicate))
                .subscribe((params) => {
                    this.prevParams = params;
                    let httpParams = new HttpParams();
                    for (const [key, value] of Object.entries(params)) {
                        httpParams = httpParams.append(key, value as string);
                    }
                    this.loadDataWithParams(this.getQueryParamsFromRoute(httpParams));
                });
        } else {
            this.loadInitialData();
        }
        // Listen to route changes and reload data
        let routerEventSub = this.router?.events
            .pipe(filter((e: Event): e is RouterEvent => e instanceof NavigationEnd))
            .subscribe(() => {
                this.loadDataWithParams(this.lastUsedHttpParams);
            });
        this.subscriptions.push(routerEventSub);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => {
            if (s) {
                s.unsubscribe();
            }
        });
        this.endPolling$.next();
        this.endPolling$.complete();
    }

    registerListenerFromContext(filterListener: SbEventListener) {
        const sub = this.eventBus
            .getObservable(filterListener.topic)
            .pipe(debounceTime(1000))
            .subscribe(
                (event) => {
                    this.handleApplicableFilterEvent(event, filterListener);
                },
                (error) => console.log(error)
            );
        this.subscriptions.push(sub);
    }

    handleApplicableFilterEvent(event: SbEvent, filterListener: SbEventListener): any {
        if (
            event &&
            event.topic === filterListener.topic &&
            event.source === filterListener.source
        ) {
            this.handleFilterEvent(event);
        }
    }

    /** Override this to get access to filter event properties.
     * Call super if you still want data to be loaded from server from here.
     */
    handleFilterEvent(event: SbEvent) {
        const result = this.eventBus.getAggregatedResult(this.context.filterListeners);
        let httpParams: HttpParams = new HttpParams();
        result.forEach((tuple) => {
            const filterListener: SbEventListener = tuple.eventListener;
            const payload = tuple.payload;
            if (filterListener.queryParameters && payload) {
                httpParams = this.mapFilterPayloadToQueryParameters(
                    payload.payload,
                    filterListener.queryParameters,
                    httpParams
                );
            }
        });

        if (this.context.queryParamsFromRoute) {
            // Merge from route if exists
            httpParams = this.getQueryParamsFromRoute(httpParams);
        }
        this.loadDataWithParams(httpParams);
    }

    private allMandatoryHttpParamsIncluded(
        httpParams: HttpParams,
        queryParams: QueryParamConfig[]
    ): boolean {
        if (queryParams) {
            return queryParams
                .filter((qp) => qp.mandatory)
                .every((qp) => httpParams?.has(qp.queryParamName));
        } else {
            return true;
        }
    }

    /**
     * Gets data and starts polling.
     * Data is cached for cacheTiming seconds.
     * @param httpParams
     */
    loadDataWithParams(httpParams: HttpParams) {
        const cacheKey = httpParams.toString();
        if (this.lastUsedHttpParams && this.lastUsedHttpParams.toString() === cacheKey) {
            // loadDataWithParams called with the same parameters
            this.handleCachedData(httpParams);
            return;
        }

        this.lastUsedHttpParams = httpParams;
        this.msgs = [];

        if (
            !this.context.filterListeners ||
            this.allMandatoryHttpParamsIncluded(
                httpParams,
                this.context.filterListeners
                    .map((filterListener) => filterListener.queryParameters)
                    .reduce((p1, p2) => p1.concat(p2))
            )
        ) {
            this.isLoading = true;

            // Check if data is available in cache and within cache timing
            const cachedData = this.dataCache[cacheKey];
            if (cachedData && Date.now() - cachedData.timestamp < this.cacheTiming) {
                this.isLoading = false;
                // Use cached data
                this.processData(cachedData.data, httpParams);
                return;
            }

            this.getData(httpParams);
            this.startPollingGetData(httpParams);
        }
    }

    private startPollingGetData(httpParams: HttpParams) {
        if (this.context.poll) {
            // Start polling after first load if configured.
            // If we are calling loadData with new httpParameters we want to kill any old poll and start a new one.
            this.endPolling$.next();
            // Start new timer with the current httpParameters.
            timer(this.context.poll?.pollIntervalMs, this.context.poll?.pollIntervalMs)
                .pipe(
                    // Don't refresh data after set number of polls (if set, else keep polling until destroy).
                    filter((num) =>
                        this.context.poll.endAfter
                            ? num < this.context.poll.endAfter
                            : true
                    ),
                    map(() => this.getData(httpParams)),
                    takeUntil(this.endPolling$)
                )
                .subscribe();
        }
    }

    private handleCachedData(httpParams: HttpParams) {
        const cachedData = this.dataCache[httpParams.toString()];
        if (cachedData) {
            this.processData(cachedData.data, httpParams);
            this.startPollingGetData(httpParams);
        } else {
            console.warn(
                'Cached data does not exist for these parameters: ' +
                    httpParams.toString()
            );
        }
    }

    /**
     * Retrieves data based on the application's synchronization state.
     * If synchronization is on, it retrieves data using the local storage service,
     * managing loading and synchronization flags.
     * If synchronization is off, it retrieves data directly from the data service,
     * caching it.
     * In both cases, it processes the response data.
     * It also handles errors and updates the last used HTTP parameters.
     * @param httpParams
     * @private
     */
    private getData(httpParams: HttpParams) {
        this.lastUsedHttpParams = httpParams;
        this.isLoading = true;

        if (this.isSynchronizationOn) {
            const flags = {
                isLoading: this.isLoading,
                isSynchronizing: this.isSynchronizing,
                lastSynchronization: this.lastSynchronization
            };

            flags.isSynchronizing = true;
            flags.isLoading = true;
            this.isLoading = flags.isLoading;
            this.isSynchronizing = flags.isSynchronizing;

            const params = this.getLocalStorageParams(httpParams);
            const getDataSubs = concat(
                this.localStorageService.getLocalStorageCache(params, flags),
                this.localStorageService.getLiveData(params, flags)
            ).subscribe((responseData) => {
                this.processData(responseData, httpParams);
                this.isLoading = flags.isLoading;
                this.isSynchronizing = flags.isSynchronizing;
                this.lastSynchronization = flags.lastSynchronization;
            });

            this.subscriptions.push(getDataSubs);
        } else {
            this.dataService
                .getDataWithParams(
                    this.context.path,
                    httpParams,
                    SbHost.WidgetDataBaseUrl,
                    this.context.widgetId
                )
                .subscribe(
                    (responseData) => {
                        this.isLoading = false;
                        this.noDataMessage = true;
                        this.dataCache[httpParams.toString()] = {
                            data: responseData,
                            timestamp: Date.now()
                        };
                        this.processData(responseData, httpParams);
                    },
                    (error) => {
                        this.isLoading = false;
                        this.responseErrorHandler(error);
                    }
                );
        }
    }

    private getLocalStorageParams(httpParams: HttpParams) {
        const params: LocalStorageParams = {
            userName: SbStorageService.userName,
            path: this.context.path,
            httpParams: httpParams,
            defaultBasePath: SbHost.WidgetDataBaseUrl
        };
        return params;
    }

    private processData(responseData, httpParams: HttpParams) {
        this.responseHandler(responseData);
        this.emitStandardHeaderInfoText(httpParams);
    }

    private emitStandardHeaderInfoText(httpParams: HttpParams) {
        this.headerInfo.emit(this.getHeaderText(httpParams));
    }

    // Override this if we need to add some other properties in header info text.
    protected getHeaderText(httpParams: HttpParams): string {
        if (httpParams.has('toDate')) {
            let headerText = '';
            // Assuming toDate always exists if fromDate exists.
            if (httpParams.has('fromDate')) {
                const fromDate = new Date(httpParams.get('fromDate'));
                headerText += fromDate.toLocaleDateString() + ' - ';
            }
            const toDate = new Date(httpParams.get('toDate'));
            const strippedDate = new Date(
                toDate.getUTCFullYear(),
                toDate.getUTCMonth(),
                toDate.getUTCDate()
            );
            headerText += strippedDate.toLocaleDateString();
            return headerText;
        }
        return '';
    }

    mapFilterPayloadToQueryParameters(
        payload: any,
        queryParameters: QueryParamConfig[],
        optionalHttpParams?: HttpParams
    ): HttpParams {
        let httpParams: HttpParams = optionalHttpParams || new HttpParams();
        queryParameters.reduce((accumulator, paramConfig) => {
            const paramName = paramConfig.queryParamName;
            const value: any = get(payload, paramConfig.sourcePath);

            if (Array.isArray(value)) {
                httpParams = value.reduce((valueAccumulator, item) => {
                    return valueAccumulator.append(
                        paramName,
                        this.getFormattedHttpParameter(item)
                    );
                }, httpParams);
            } else if (value) {
                httpParams = httpParams.append(
                    paramName,
                    this.getFormattedHttpParameter(value)
                );
            }
            return accumulator;
        }, httpParams);
        return httpParams;
    }
    private getFormattedHttpParameter(value) {
        return value instanceof Date ? value.toJSON() : value;
    }

    loadInitialData() {
        let httpParams = new HttpParams();
        if (this.context.queryParamsFromRoute) {
            httpParams = this.getQueryParamsFromRoute(httpParams);
            this.context.queryParamsFromRoute.forEach((qp) => {
                const routeValue = this.optionalRouteParams.get(qp.sourcePath);
                if (routeValue) {
                    const param = qp.queryParamName;
                    const value = this.optionalRouteParams.get(qp.sourcePath);
                    // Do not add the same value again
                    if (!(httpParams.has(param) && httpParams.get(param) === value)) {
                        httpParams = httpParams.append(param, value);
                    }
                }
            });
        }
        this.loadDataWithParams(httpParams);
    }

    getQueryParamsFromRoute(httpParams: HttpParams): HttpParams {
        this.context.queryParamsFromRoute?.forEach((qp) => {
            const routeValue = this.optionalRouteParams.get(qp.sourcePath);
            if (routeValue && !httpParams.has(qp.queryParamName)) {
                httpParams = httpParams.append(
                    qp.queryParamName,
                    this.optionalRouteParams.get(qp.sourcePath)
                );
            }
        });
        return httpParams;
    }

    defaultErrorHandler(error: any) {
        this.msgs = [];
        this.msgs.push({
            severity: 'error',
            summary: error.title,
            detail: error.message
        });
        console.error(`Failed to fetch data: ${JSON.stringify(error)}`);
    }
}
